import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faUpload, faEdit, faToggleOn, faToggleOff,
  faSpinner, faPercent, faSearch, faSync, faBuilding, faTimes,
  faFilter, faChevronDown, faTrash
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import CSVUploadModal from '../../components/Invoicing/CSVUploadModal';
import TradeOpsDeleteModal from '../../components/TradeOpsDeleteModal';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSButton from '../../components/ui/HMSButton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import {
  fetchVATRatesByEntity, createVATRate, updateVATRate, deleteVATRate,
  uploadVATRatesCSV, fetchHMSEntities,
} from '../../services/invoicingService';
import ListLoadError from '../../components/ListLoadError';
import { extractApiError } from '../../utils/errorMessages';
import { useFlashMessage } from '../../components/FlashMessage';
import { useAuth } from '../../context/AuthContext';
import './OutgoingInvoice.css';
import '../../components/Invoicing/InvoiceSearchFilter.css';

const TEMPLATE_COLUMNS = ['VAT Code', 'Description', 'Rate Percentage', 'Country Code'];

const VATRatesPage = () => {
  const history = useHistory();
  const { user } = useAuth();
  const isFinancialApprover = user?.is_financial_approver || user?.is_superuser;
  const { showError, showSuccess } = useFlashMessage();
  const [rates, setRates] = useState([]);
  const [entities, setEntities] = useState([]);
  // A company list that failed to load must say so. Swallowed, it left no
  // company to choose and an empty table that read as "no rates" -- which
  // is what Finance-only staff saw while the list refused them (ledger row 124).
  const [entitiesError, setEntitiesError] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState({ is_active: '', country_code: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { loadEntities(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const loadEntities = async () => {
    setEntitiesError(null);
    try {
      const data = await fetchHMSEntities();
      const list = data.results || data || [];
      setEntities(list);
      if (list.length === 1) setSelectedEntity(String(list[0].id));
    } catch (err) {
      console.error('Failed to load entities:', err);
      setEntitiesError(extractApiError(err, 'The list of companies could not be loaded, so no company can be chosen.'));
    }
  };

  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const activeFiltersCount = Object.keys(activeFilters).length;

  const loadRates = useCallback(async (page = 1, append = false) => {
    if (!selectedEntity) { setRates([]); setTotalCount(0); setHasMore(false); return; }
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const data = await fetchVATRatesByEntity(selectedEntity, {
        showInactive: true, page, search: debouncedSearch || undefined,
        filters: activeFiltersCount > 0 ? activeFilters : undefined,
      });
      const items = data.results || data || [];
      if (append) {
        setRates(prev => [...prev, ...items]);
      } else {
        setRates(items);
      }
      setTotalCount(data.count ?? items.length);
      setHasMore(!!data.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load VAT rates:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedEntity, debouncedSearch, activeFiltersCount]);

  useEffect(() => { loadRates(1, false); }, [loadRates]);

  const handleLoadMore = () => { loadRates(currentPage + 1, true); };

  const openCreateModal = () => {
    setEditingRate(null);
    setFormData({
      vat_code: '', description: '', rate_percentage: '',
      country_code: '', use_case: '',
      valid_from: new Date().toISOString().split('T')[0], valid_to: '',
    });
    setShowCreateModal(true);
  };

  const openEditModal = (rate) => {
    setEditingRate(rate);
    setFormData({
      vat_code: rate.vat_code,
      description: rate.description,
      rate_percentage: rate.rate_percentage,
      country_code: rate.country_code || '',
      use_case: rate.use_case || '',
      valid_from: rate.valid_from || '',
      valid_to: rate.valid_to || '',
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.vat_code || !formData.description || !formData.rate_percentage || !formData.valid_from) {
      showError('VAT Code, Description, Rate %, and Valid From are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        rate_percentage: parseFloat(formData.rate_percentage),
        valid_to: formData.valid_to || null,
      };
      if (editingRate) {
        await updateVATRate(editingRate.id, payload);
        showSuccess('VAT rate updated successfully.');
      } else {
        payload.entity_id = parseInt(selectedEntity);
        await createVATRate(payload);
        showSuccess('VAT rate created and assigned to entity.');
      }
      setShowCreateModal(false);
      loadRates(1, false);
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        showError(Object.values(detail).flat().join(', '));
      } else {
        showError('Failed to save VAT rate.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (rate) => {
    try {
      await updateVATRate(rate.id, { is_active: !rate.is_active });
      loadRates(1, false);
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleUpload = async (file, entityId) => await uploadVATRatesCSV(file, entityId);

  const handleUploadClose = () => {
    setShowUploadModal(false);
    loadRates(1, false);
  };

  const openDeleteModal = (rate) => {
    setDeleteTarget(rate);
    setDeleteWarning(false);
    setDeleteWarningMsg('');
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteVATRate(deleteTarget.id);
      showSuccess('VAT rate permanently deleted.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadRates(1, false);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.blockers) {
        const lines = data.blockers.map(b => b.type).join(', ');
        setDeleteWarningMsg(`${data.message}\n\nReferenced by: ${lines}`);
        setDeleteWarning(true);
      } else if (err.response?.status === 403) {
        setDeleteWarningMsg(data?.error || 'Permission denied.');
        setDeleteWarning(true);
      } else {
        showError(data?.error || 'Delete failed.');
        setShowDeleteModal(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Entity tabs for the shared page header. These pick the entity whose VAT
  // rates are listed — same behaviour as the old strip, search included.
  const entityTabs = entities.map(e => ({
    key: String(e.id),
    label: e.hms_entity_name,
    icon: faBuilding,
  }));

  const selectEntity = (id) => {
    setSelectedEntity(id);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container master-data-screen vat-rates-screen">
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          title="VAT Rates"
          kicker="Finance"
          tabs={entities.length > 0
            ? { items: entityTabs, activeKey: selectedEntity, onChange: selectEntity, alwaysLabel: true }
            : undefined}
          actions={
            <>
              <button className="btn-secondary" onClick={() => setShowUploadModal(true)} disabled={!selectedEntity}>
                <FontAwesomeIcon icon={faUpload} /> Upload CSV
              </button>
              <button className="btn-primary" onClick={openCreateModal} disabled={!selectedEntity}>
                <FontAwesomeIcon icon={faPlus} /> Create VAT Rate
              </button>
            </>
          }
        />

        {entitiesError && (
          <div style={{ padding: '0 2rem' }}>
            <ListLoadError message={entitiesError} onRetry={loadEntities} />
          </div>
        )}

        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <div className="invoice-search-filter">
              <div className="search-header">
                <div className="search-input-group">
                  <div className="search-input-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder={selectedEntity ? "Search by code, description, country, or use case..." : "Select an entity above..."}
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={!selectedEntity}
                      className="search-input compact"
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm('')}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                    disabled={!selectedEntity}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    Filters
                    {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
                    <FontAwesomeIcon icon={faChevronDown} className={`chevron ${showFilters ? 'rotated' : ''}`} />
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="search-filters-panel">
                  <div className="filters-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select value={filters.is_active} onChange={(e) => setFilters(f => ({ ...f, is_active: e.target.value }))} className="form-select">
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Country Code</label>
                      <select value={filters.country_code} onChange={(e) => setFilters(f => ({ ...f, country_code: e.target.value }))} className="form-select">
                        <option value="">All Countries</option>
                        {[...new Set(rates.map(r => r.country_code).filter(Boolean))].sort().map(cc => (
                          <option key={cc} value={cc}>{cc}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="filters-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => { loadRates(1, false); setShowFilters(false); }}>Apply</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setFilters({ is_active: '', country_code: '' }); setSearchTerm(''); }} disabled={activeFiltersCount === 0 && !searchTerm}>Clear All</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className="reload-button" onClick={() => loadRates(1, false)} title="Reload VAT rates" disabled={!selectedEntity}>
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>

        <div className="invoice-list-container">
          {!selectedEntity ? (
            /* `card` because the block this replaces drew its own white panel
               around the centred text. */
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faPercent} />}
              title="Select an Entity"
              hint="Choose an entity above to view its VAT rates"
            />
          ) : loading ? (
            /* Skeleton in the shape of the VAT-rate table that is coming.
               `.loading-state` keeps the padding the spinner block used; only
               its centring has to go, or the bars collapse to their content
               width. */
            <div className="loading-state" style={{ alignItems: 'stretch' }}>
              <HMSSkeleton shape="title" />
              <HMSSkeleton lines={6} />
            </div>
          ) : rates.length === 0 ? (
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faPercent} />}
              title={searchTerm ? 'No Results' : 'No VAT Rates'}
              hint={searchTerm ? 'No VAT rates match your search' : 'No VAT rates assigned to this entity. Upload a CSV or create manually.'}
            />
          ) : (
            <>
            <table className="invoice-list-table">
              <thead>
                <tr>
                  <th>VAT Code</th>
                  <th>Description</th>
                  <th>Rate %</th>
                  <th>Country</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map(rate => (
                  <tr key={rate.id}>
                    <td className="invoice-number-cell">{rate.vat_code}</td>
                    <td>{rate.description}</td>
                    <td>{rate.rate_percentage}%</td>
                    <td>{rate.country_code || '-'}</td>
                    <td>{rate.valid_from || '-'}</td>
                    <td>{rate.valid_to || 'Indefinite'}</td>
                    <td>
                      <HMSBadge status={rate.is_active ? 'approved' : 'draft'}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </HMSBadge>
                    </td>
                    <td className="actions-cell">
                      <div
                        className={`action-dropdown ${activeDropdown === rate.id ? 'dropdown-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        ref={activeDropdown === rate.id ? dropdownRef : null}
                      >
                        <button
                          className="gear-button"
                          onClick={(e) => { e.stopPropagation(); toggleDropdown(rate.id); }}
                        >
                          Actions
                        </button>
                        {activeDropdown === rate.id && (
                          <div className="dropdown-menu">
                            <button onClick={() => { setActiveDropdown(null); openEditModal(rate); }}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <hr />
                            <button onClick={() => { setActiveDropdown(null); handleToggleActive(rate); }}>
                              <FontAwesomeIcon icon={rate.is_active ? faToggleOff : faToggleOn} />
                              {rate.is_active ? ' Deactivate' : ' Activate'}
                            </button>
                            {isFinancialApprover && (
                              <>
                                <hr />
                                <button className="delete-action" onClick={() => openDeleteModal(rate)}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="master-data-load-more">
              <span className="load-more-count">
                Showing {rates.length.toLocaleString()} of {totalCount.toLocaleString()}
              </span>
              {hasMore && (
                <HMSButton variant="ghost" size="compact" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : 'Show 100 more'}
                </HMSButton>
              )}
            </div>
            </>
          )}
        </div>

        <Footer />
      </div>

      {showCreateModal && (
        <div className="md-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-header-text">
                <FontAwesomeIcon icon={editingRate ? faEdit : faPlus} className="md-modal-header-icon" />
                <h2>{editingRate ? 'Edit VAT Rate' : 'Create VAT Rate'}</h2>
              </div>
              <button className="md-modal-close" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-form-label">VAT Code <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.vat_code} onChange={(e) => setFormData({ ...formData, vat_code: e.target.value.toUpperCase() })} placeholder="e.g. VAT_STD_19, EXEMPT" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Standard VAT Rate 19%" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Rate % <span className="required">*</span></label>
                <input className="md-form-input" type="number" min="0" max="100" step="0.01" value={formData.rate_percentage} onChange={(e) => setFormData({ ...formData, rate_percentage: e.target.value })} placeholder="e.g. 19.00" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Country Code</label>
                <input className="md-form-input" type="text" value={formData.country_code} onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })} placeholder="e.g. DE, ZA, CH" maxLength={10} />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Valid From <span className="required">*</span></label>
                <InvoiceDatePicker
                  name="valid_from"
                  id="vat_valid_from"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  placeholder="Select start date..."
                />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Valid To</label>
                <InvoiceDatePicker
                  name="valid_to"
                  id="vat_valid_to"
                  value={formData.valid_to || ''}
                  onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  placeholder="Indefinite if left blank"
                />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Use Case</label>
                <input className="md-form-input" type="text" value={formData.use_case} onChange={(e) => setFormData({ ...formData, use_case: e.target.value })} placeholder="When to apply this rate" />
              </div>
            </div>
            <div className="md-modal-footer">
              <button className="md-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="md-btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingRate ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CSVUploadModal
        isOpen={showUploadModal} onClose={handleUploadClose} onUpload={handleUpload}
        title="Upload VAT Rates" templateColumns={TEMPLATE_COLUMNS}
        entityRequired={true} entities={entities} selectedEntity={selectedEntity}
      />

      <TradeOpsDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        itemType="master_data"
        itemDetails={deleteTarget ? {
          recordType: 'VAT Rate',
          recordCode: deleteTarget.vat_code,
          recordName: deleteTarget.description,
        } : {}}
        isWarning={deleteWarning}
        warningMessage={deleteWarningMsg}
        isDeleting={isDeleting}
      />
    </Layout>
  );
};

export default VATRatesPage;
